import { useState } from "react"
import { Alert, Button, Card, Empty, Input, Modal, Skeleton, Tag } from "antd"
import { Plus, Star } from "lucide-react"
import { Link, useNavigate } from "react-router-dom"
import type { DashboardSummary } from "@csbi/contracts"
import { useCreateDashboard, useDashboards } from "../../features/dashboards/use-dashboards.js"
import { PageHeader } from "../../shared/components/page-header.js"

export function DashboardsPage() {
  const { data, isLoading, error } = useDashboards()
  const [creating, setCreating] = useState(false)
  const dashboards = data?.dashboards ?? []

  return (
    <>
      <PageHeader
        title="Dashboards"
        extra={
          <Button size="small" type="primary" icon={<Plus size={14} />} onClick={() => setCreating(true)}>
            New dashboard
          </Button>
        }
      />

      {isLoading && <Skeleton active paragraph={{ rows: 6 }} />}
      {error && <Empty description="Could not load dashboards" />}

      {!isLoading && !error && dashboards.length === 0 && (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={
            <span style={{ fontSize: 13, color: "var(--csbi-ink-secondary)" }}>
              No dashboards yet. Create one, then add tiles from any metric domain.
            </span>
          }
        >
          <Button type="primary" icon={<Plus size={14} />} onClick={() => setCreating(true)}>
            New dashboard
          </Button>
        </Empty>
      )}

      {dashboards.length > 0 && <DashboardGrid dashboards={dashboards} />}

      <NewDashboard open={creating} onClose={() => setCreating(false)} />
    </>
  )
}

function NewDashboard({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [name, setName] = useState("")
  const navigate = useNavigate()
  const create = useCreateDashboard()

  const submit = () =>
    create.mutate(
      { name: name.trim() },
      {
        onSuccess: ({ slug }) => {
          setName("")
          onClose()
          navigate(`/dashboards/${encodeURIComponent(slug)}`)
        },
      },
    )

  return (
    <Modal
      open={open}
      title="New dashboard"
      okText="Create"
      okButtonProps={{ disabled: name.trim().length === 0, loading: create.isPending }}
      onOk={submit}
      onCancel={() => {
        setName("")
        onClose()
      }}
    >
      <Input
        value={name}
        onChange={(e) => setName(e.target.value)}
        onPressEnter={submit}
        maxLength={80}
        autoFocus
        placeholder="e.g. Nordics weekly"
      />
      {create.isError && (
        <Alert
          type="error"
          showIcon
          style={{ marginTop: 12 }}
          message={create.error instanceof Error ? create.error.message : "Could not create the dashboard."}
        />
      )}
    </Modal>
  )
}

const THREE_UP = "repeat(auto-fit, minmax(max(260px, (100% - 24px) / 3), 1fr))"

function DashboardGrid({ dashboards }: { dashboards: readonly DashboardSummary[] }) {
  return (
    <div style={{ display: "grid", gap: 12, gridTemplateColumns: THREE_UP }}>
      {dashboards.map((d) => (
          <Link key={d.id} to={`/dashboards/${encodeURIComponent(d.slug)}`} style={{ color: "inherit" }}>
            <Card hoverable size="small" style={{ height: "100%" }}>
              <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
                <span style={{ fontSize: 15, fontWeight: 650, color: "var(--csbi-ink-primary)", minWidth: 0 }}>
                  {d.name}
                </span>
                {/* The home one has to be recognisable at a glance — otherwise "which one did I set" means opening them one by one */}
                {d.isHome && (
                  <Tag color="blue" icon={<Star size={11} />} style={{ marginLeft: "auto", marginInlineEnd: 0 }}>
                    Home
                  </Tag>
                )}
              </div>
              {d.description && (
                <p style={{ margin: "6px 0 0", fontSize: 12, lineHeight: 1.6, color: "var(--csbi-ink-secondary)" }}>
                  {d.description}
                </p>
              )}
              <p
                style={{
                  margin: "10px 0 0",
                  fontSize: 12,
                  color: "var(--csbi-ink-secondary)",
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {d.tileCount} {d.tileCount === 1 ? "tile" : "tiles"}
              </p>
            </Card>
          </Link>
      ))}
    </div>
  )
}
